import React, { useEffect, useState } from "react";
import Button, {
  ButtonTypes,
  ButtonColorTypes,
} from "../../shared-components/Button";
import InputField from "../../shared-components/InputField";

import { uniqueUUID } from "../../utils";
import { PersonFormStateError } from "../PersonFormErrorState";
import PersonFormStringDate from "../PersonFormStringDate";
import XIcon from "../../shared-components/icons/XIcon";

export interface EducationDefinitions {
  degree: string;
  institution: string;
  startDate: string;
  endDate: string;
  country: string;
  key: string;
}

type Props = {
  onChange?: (aliases: EducationDefinitions[]) => void;
  error?: PersonFormStateError;
  prefill?: EducationDefinitions[];
  alternateColor?: boolean;
};

function buildEmptyDefinition(): EducationDefinitions {
  return {
    degree: "",
    institution: "",
    startDate: "",
    endDate: "",
    country: "",
    key: uniqueUUID(),
  };
}

const PersonFormEducation = ({
  onChange,
  error,
  prefill,
  alternateColor,
}: Props) => {
  const [fieldsList, setFields] = useState<EducationDefinitions[]>([]);

  const appendFields = () => {
    handleChange([...fieldsList, buildEmptyDefinition()]);
  };

  const updateField = (
    idx: number,
    field: "degree" | "institution" | "startDate" | "endDate" | "country",
    value: string
  ) => {
    fieldsList[idx][field] = value;
    handleChange([...fieldsList]);
  };

  const removeField = (idx: number) => {
    fieldsList.splice(idx, 1);
    handleChange([...fieldsList]);
  };

  const handleChange = (list: EducationDefinitions[]) => {
    onChange?.(list);
    setFields(list);
  };

  const handleAdd = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    appendFields();
  };

  const handleRemove = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    idx: number
  ) => {
    e.preventDefault();
    removeField(idx);
  };

  useEffect(() => {
    if (prefill && prefill.length >= 1) {
      handleChange([...prefill]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  return (
    <div className="flex">
      {fieldsList.length > 0 && (
        <div className="flex-1 mr-4">
          {fieldsList.map((fields, idx) => (
            <div key={fields.key} className="flex flex-wrap items-center">
              <InputField
                handleChange={(e) => {
                  updateField(idx, "degree", e.target.value);
                }}
                name={`degree-${idx}`}
                value={fieldsList[idx].degree}
                placeholder=""
                label={"Degree"}
                classes="mr-4 flex-1"
              />
              <InputField
                handleChange={(e) => {
                  updateField(idx, "institution", e.target.value);
                }}
                name={`institution-${idx}`}
                placeholder=""
                value={fieldsList[idx].institution}
                label={"Name of Institution"}
                classes={"mr-4 flex-1"}
              />
              <PersonFormStringDate
                index={idx}
                error={error}
                _name={`start-date-{idx}`}
                _label="Start Date"
                prefill={fieldsList[idx].startDate}
                onChange={(e) => {
                  updateField(idx, "startDate", e);
                }}
              ></PersonFormStringDate>
              <PersonFormStringDate
                error={error}
                _name={`end-date-{idx}`}
                _label="End Date"
                index={idx}
                prefill={fieldsList[idx].endDate}
                onChange={(e) => {
                  updateField(idx, "endDate", e);
                }}
              ></PersonFormStringDate>
              <InputField
                handleChange={(e) => {
                  updateField(idx, "country", e.target.value);
                }}
                name={`country-${idx}`}
                placeholder=""
                value={fieldsList[idx].country}
                label={"Country"}
                classes={"mr-4"}
              />
              {idx >= 0 && (
                <span className="pl-2 h-0">
                  <div
                    className="rounded-full bg-gray-50 border p-1 text-gray-500 cursor-pointer"
                    onClick={(e) => handleRemove(e, idx)}
                  >
                    <XIcon />
                  </div>
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="self-end w-44">
        <Button
          type={ButtonTypes.add}
          text="Add Education"
          className="w-44"
          colorType={
            alternateColor
              ? ButtonColorTypes.alternate
              : ButtonColorTypes.action
          }
          onClick={handleAdd}
        />
      </div>
    </div>
  );
};

export default PersonFormEducation;
